<template>
  <div class="pie-chart">
    <ApexChart type="pie" :height="height" :options="chartOptions" :series="series" class="apex-chart" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ApexChart from 'vue3-apexcharts';
import { stateColorMap } from '@/Modules/Grafer/common/stateMappings';
import {
  commonChart
} from '@/Modules/Grafer/common/chartDefaults';

const series = computed(() => props.data.map(item => item.value));
const labels = computed(() => props.data.map(item => item.name))

const computedColors = computed(() => {
  return props.colors.length
    ? props.colors
    : labels.value.map(label => stateColorMap[label] || '#999')
})
const total = computed(() => props.data.reduce((sum, item) => sum + item.value, 0));


const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  height: {
    type: Number,
    default: 360
  },
  innerRadius: {
    type: Number,
    default: 0
  },
  colors: {
    type: Array,
    default: () => []
  }
});


const chartOptions = computed(() => ({
  chart: {
    type: 'pie',
    height: props.height,
    ...commonChart,
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 600,
      animateGradually: { enabled: true, delay: 100 },
      dynamicAnimation: { enabled: true, speed: 400 }
    }
  },
  stroke: {
    show: false
  },
  colors: computedColors.value,
  labels: labels.value,
  dataLabels: { enabled: false },
  plotOptions: {
    pie: {
      expandOnClick: false, // disable click scaling
      offsetY: 20,
      donut: {
        size: props.innerRadius > 0
          ? `${(props.innerRadius / (props.height / 2)) * 100}%`
          : '0%'
      }
    }
  },
  states: {
    hover: {
      filter: {
        type: 'none',
      }
    },
    active: {
      filter: {
        type: 'none',
      }
    }
  },
  tooltip: {
    theme: 'light',
    style: { fontSize: '14px' },
    y: {
      formatter: (value) => {
        const percentage = ((value / total.value) * 100).toFixed(1);
        return `${value} (${percentage}%)`;
      }
    },
    custom: ({ series, seriesIndex, w }) => {
      const value = series[seriesIndex];
      const label = w.globals.labels[seriesIndex];
      const percentage = ((value / total.value) * 100).toFixed(1);
      const color = computedColors.value[seriesIndex];
      return `
        <div class="custom-tooltip">
          <div class="tooltip-item">
            <span class="tooltip-dot" style="background-color: ${color}"></span>
            <span class="tooltip-label">${label}:</span>
            <span class="tooltip-value">${value} (${percentage}%)</span>
          </div>
        </div>
      `;
    }
  },
  legend: { show: false },
}));
</script>

<style scoped>
.pie-chart {
  position: relative;
  width: 100%;
  max-width: 426px;
  margin: 0 auto;
}

:deep(.apexcharts-series path) {
  opacity: 0.9;
  transform-origin: 50% 50%;
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.4s ease;
}

:deep(.apexcharts-series path:hover) {
  transform: scale(1.08) translate(0.6%, 0%);
  opacity: 1;
}

:global(.custom-tooltip) {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 120px;
}

:global(.tooltip-item) {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

:global(.tooltip-item:last-child) {
  margin-bottom: 0;
}

:global(.tooltip-dot) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

:global(.tooltip-label) {
  color: #64748b;
  flex: 1;
  font-weight: 500;
}

:global(.tooltip-value) {
  font-weight: 600;
  color: #1e293b;
}
</style>